import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import { ReadOnlyProvider } from '@/contexts/ReadOnlyContext';

interface PartnerProtectedRouteProps {
  children: ReactNode;
}

const PartnerProtectedRoute = ({ children }: PartnerProtectedRouteProps) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isPartnerUser, isLoading: partnerLoading, isDeactivated } = usePartnerAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (authLoading || partnerLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to partner auth if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/partner/auth" replace state={{ from: location }} />;
  }

  // Redirect to partner auth with error if authenticated but not a partner user
  if (!isPartnerUser) {
    return <Navigate to="/partner/auth" replace state={{ accessDenied: true }} />;
  }

  // Wrap with ReadOnlyProvider - deactivated accounts get read-only access
  return (
    <ReadOnlyProvider isDeactivated={isDeactivated} accountType="partner">
      {children}
    </ReadOnlyProvider>
  );
};

export default PartnerProtectedRoute;
